/**
 * The entry lifecycle's pure half (P7-T1): the reviewed impact, the two request bodies, and the
 * autocomplete reuse rule.
 *
 * Three decisions are made here and nowhere else in `apps/web`:
 *
 *   - **the impact is the API's.** A stored `entry.delete` / `entry.duplicate` preview carries the
 *     account, the entry's own signed amount, how the derived balance moves, whether the row
 *     contributes today and whether the commit needs the acknowledgement. The surface renders
 *     those figures and this module only words them — it never re-derives a balance, because the
 *     preview is the *only* place that decision runs (`apps/api/src/ledger/lifecycle.ts`,
 *     `planLifecycle`);
 *   - **the acknowledgement is visible.** A commit that removes a figure already effective is
 *     refused `409` without `impactAcknowledged`, and that refusal is the protection: the surface
 *     asks for the acknowledgement *because the impact says so*, and it never sends `true` on its
 *     own initiative for a row the API did not flag;
 *   - **reuse never silently replaces.** Picking a suggestion fills the description, and it fills
 *     the amount, the account and the category only where the user has not already decided them —
 *     naming every value it kept instead, a kept account or a category under the display name the
 *     caller supplies (`KeptLabelOf`) rather than under an internal id. The dates are never part of
 *     a suggestion at all, so they are never touched (`UX-FLOWS.md` §4: "Autocomplete suggests
 *     values and must not silently replace account, dates or amount").
 */

import type {
  EntryBody,
  EntryDeleteRequest,
  EntryDuplicateRequest,
  EntryImpactBody,
  EntryLifecyclePreviewBody,
  EntrySuggestionBody,
  ReferenceBlockerBody,
} from '@saldo/contracts';

import type { EntryForm, Mapped } from './forms';
import { formatBalance, formatMoney, formatAmountInput } from './money';

/* --------------------------------------------------------------------- the impact --- */

/** One reference the preview carries, in the API's own vocabulary. */
export interface ImpactReference {
  readonly kind: string;
  readonly count: number;
}

/** The consequential impact a stored preview computed, as the surface renders it. */
export interface LifecycleImpactView {
  readonly accountId: string;
  readonly currency: string;
  /** The entry's own signed amount, unchanged by the previewed action. */
  readonly signedMinor: bigint;
  /** How the account's derived balance moves: the API's own figure. */
  readonly balanceDeltaMinor: bigint;
  readonly paid: boolean | null;
  /** Whether the entry contributes to any figure today, under the one eligibility rule. */
  readonly effective: boolean;
  /** True when this commit destroys a figure that is already effective. */
  readonly requiresConfirmation: boolean;
  /** The rows the action touches or leaves alone — never the rows themselves. */
  readonly references: readonly ImpactReference[];
}

export interface LifecyclePreviewView {
  readonly id: string;
  readonly action: 'entry.delete' | 'entry.duplicate';
  readonly entryId: string;
  readonly revision: string;
  readonly businessToday: string;
  readonly expiresAt: string;
  readonly canceled: boolean;
  readonly impact: LifecycleImpactView;
  /** What this commit does, in words, from the API's own flags and figures. */
  readonly consequence: string;
  readonly acknowledgementLabel: string;
}

function impactView(impact: EntryImpactBody): LifecycleImpactView {
  return {
    accountId: impact.accountId,
    currency: impact.currency,
    signedMinor: BigInt(impact.signedMinor),
    balanceDeltaMinor: BigInt(impact.balanceDeltaMinor),
    paid: impact.paid ?? null,
    effective: impact.effective,
    requiresConfirmation: impact.requiresConfirmation,
    references: impact.references.map((reference: ReferenceBlockerBody) => ({
      kind: reference.kind,
      count: reference.count,
    })),
  };
}

/**
 * The sentence the review shows beside the figures.
 *
 * Every branch reads the API's own two flags (`effective`, `requiresConfirmation`) and its own
 * delta; none of them decides anything. A row that contributes nothing today says so plainly,
 * because "this moves no figure" is the honest reading of a pending row and it is exactly what a
 * deliberate confirmation is not needed for.
 */
export function lifecycleConsequence(
  action: LifecyclePreviewView['action'],
  impact: LifecycleImpactView,
): string {
  const amount = formatMoney(impact.signedMinor, impact.currency);
  if (action === 'entry.delete') {
    return impact.effective
      ? `Cancelling this ${amount} entry removes it from the recorded balance: the account moves by ${formatBalance(impact.balanceDeltaMinor, impact.currency)}. The row is kept and stays readable; it never contributes again until it is restored.`
      : `This ${amount} entry is not contributing today, so cancelling it moves no recorded figure. The row is kept and stays readable, and it can be restored.`;
  }
  if (action === 'entry.duplicate') {
    const move = impact.effective
      ? `it is effective at the business day, so the account moves by ${formatBalance(impact.balanceDeltaMinor, impact.currency)}`
      : 'it is pending until its due date, so it moves no figure today';
    const inherited =
      impact.references.length === 0
        ? ''
        : ` It does not inherit ${impact.references.map((reference) => reference.kind).join(' or ')}.`;
    // The copy is never paid, whatever the source was — the clause "cannot silently mark an entry
    // paid" is about the copy, so the surface states it whether or not there is an identity to
    // inherit, and states it before what the copy does to a figure.
    return `The copy is a new ordinary entry of ${amount}, and it is never born paid: ${move}.${inherited}`;
  }
  const inherited =
    impact.references.length === 0
      ? ''
      : ` The copy does not inherit ${impact.references.map((reference) => reference.kind).join(' or ')}.`;
  return impact.effective
    ? `The copy is a new ordinary entry of ${amount} and it is effective at the business day, so the account moves by ${formatBalance(impact.balanceDeltaMinor, impact.currency)}.${inherited}`
    : `The copy is a new ordinary entry of ${amount}, pending until its due date, so it moves no figure today.${inherited}`;
}

/** The acknowledgement's own words; the surface shows them only when the API asks for it. */
export function acknowledgementLabel(impact: LifecycleImpactView): string {
  return `I have read this impact: ${formatBalance(impact.balanceDeltaMinor, impact.currency)} moves in the account now.`;
}

/**
 * Reduce a stored lifecycle preview to what the surface reviews.
 *
 * Nothing is recomputed: `balanceDeltaMinor` is the API's own figure, and `requiresConfirmation` is
 * the API's own answer to "will this commit need an acknowledgement?".
 */
export function lifecyclePreviewView(preview: EntryLifecyclePreviewBody): LifecyclePreviewView {
  const impact = impactView(preview.impact);
  return {
    id: preview.id,
    action: preview.action,
    entryId: preview.entryId,
    revision: preview.revision,
    businessToday: preview.businessToday,
    expiresAt: preview.expiresAt,
    canceled: preview.canceled,
    impact,
    consequence: lifecycleConsequence(preview.action, impact),
    acknowledgementLabel: acknowledgementLabel(impact),
  };
}

/* ------------------------------------------------------------------ the requests --- */

/**
 * Whether this row is an ordinary entry the one optional category belongs on.
 *
 * A special row — an opening, a transfer leg — carries none, so the editor says so rather than
 * offering a control the API would refuse (`entrySchema`'s `categoryId`).
 */
export function carriesCategory(entry: EntryBody): boolean {
  return entry.type === 'income' || entry.type === 'expense';
}

/**
 * `entry.delete`: the row to cancel, or the reason this surface does not offer it.
 *
 * The four refusals are the API's own (`planLifecycle`): a transfer leg's cancellation belongs to
 * its pair, an opening is *replaced* rather than deleted, an installment member's belongs to its
 * group, and an already-canceled row is restored rather than cancelled again. Saying so here costs
 * no round trip and cannot disagree with the API, which refuses all four again.
 */
export function toEntryDelete(entry: EntryBody): Mapped<EntryDeleteRequest> {
  if (entry.canceled) return { ok: false, error: 'This entry is already canceled. Restore it instead.' };
  if (entry.type === 'transfer') {
    return { ok: false, error: 'A payment leg is cancelled with its pair, not on its own.' };
  }
  if (entry.type === 'opening') {
    return { ok: false, error: 'An opening is replaced on its account, not deleted here.' };
  }
  if (entry.installmentGroupId !== null) {
    return { ok: false, error: 'An installment is deleted with its group, not on its own.' };
  }
  return { ok: true, body: { action: 'entry.delete', entryId: entry.id } };
}

/** `entry.duplicate`: the row to copy, or the reason this surface does not offer it. */
export function toEntryDuplicate(entry: EntryBody): Mapped<EntryDuplicateRequest> {
  if (entry.canceled) return { ok: false, error: 'A canceled entry cannot be duplicated; restore it first.' };
  if (entry.type === 'transfer') {
    return { ok: false, error: 'A payment leg cannot be duplicated: its pair identity is not copyable.' };
  }
  if (entry.type === 'opening') {
    return { ok: false, error: 'An opening is replaced on its account, not duplicated.' };
  }
  return { ok: true, body: { action: 'entry.duplicate', entryId: entry.id } };
}

/* ---------------------------------------------------------------------- the reuse --- */

/** Why a suggested value was not applied. */
export interface KeptField {
  readonly field: 'amount' | 'account' | 'category';
  readonly kept: string;
  readonly suggested: string;
}

/**
 * The display name of a value a reuse kept, so the note can name it.
 *
 * An account and a category are identifiers on the wire and mean nothing to the person reading the
 * sentence, so the caller — the only layer that holds the account list and the owner's categories —
 * supplies their names. `undefined` for an id falls back to the id itself.
 */
export type KeptLabelOf = (field: 'account' | 'category', id: string) => string | undefined;

export interface SuggestionReuse {
  readonly form: EntryForm;
  /** The fields the suggestion filled. */
  readonly applied: readonly string[];
  /** The fields the suggestion would have changed and did not, with what was kept. */
  readonly kept: readonly KeptField[];
  /** The one line the editor shows, so nothing is replaced silently. */
  readonly note: string;
}

export interface ReuseOptions {
  /** True when the account is a fact rather than a control, or the user has chosen one already. */
  readonly accountTouched: boolean;
  /** Names for the values a reuse can keep; see `KeptLabelOf`. Omitted, the note prints the id. */
  readonly labelOf?: KeptLabelOf;
}

/**
 * One kept field as the note prints it.
 *
 * An amount is already the number the person typed, so it is printed as it stands. An account or a
 * category is an identifier on the wire, so it is printed under the display name the caller
 * supplied — and under the id only when no name came with it.
 */
function notedValue(options: ReuseOptions, field: KeptField): string {
  if (field.field === 'amount') return field.kept;
  return options.labelOf?.(field.field, field.kept) ?? field.kept;
}

/**
 * Apply a picked suggestion to the draft.
 *
 * The rule, in one place:
 *
 *   - the **description** is what was picked, so it is always filled;
 *   - the **amount** (and, with it, the income/expense sign) is filled only when nothing has been
 *     typed; a typed amount is kept and the suggestion's own amount is named in the note;
 *   - the **account** is filled only when the user has not chosen one; otherwise it is kept and
 *     named;
 *   - the **category** is filled only when the draft has none; a chosen one is kept and named;
 *   - the **dates** are never part of a suggestion, so they are never touched, and the note says so.
 */
export function reuseSuggestion(
  current: EntryForm,
  suggestion: EntrySuggestionBody,
  options: ReuseOptions,
): SuggestionReuse {
  const applied: string[] = ['description'];
  const kept: KeptField[] = [];
  const suggestedMinor = BigInt(suggestion.signedMinor);
  const suggestedAmount = formatAmountInput(
    suggestedMinor < 0n ? -suggestedMinor : suggestedMinor,
  );

  const amountUntouched = current.amount.trim() === '';
  if (amountUntouched) {
    applied.push('amount');
  } else {
    kept.push({ field: 'amount', kept: current.amount, suggested: suggestedAmount });
  }

  if (options.accountTouched) {
    kept.push({ field: 'account', kept: current.accountId, suggested: suggestion.accountId });
  } else {
    applied.push('account');
  }

  if (current.categoryId === null) {
    applied.push('category');
  } else {
    kept.push({
      field: 'category',
      kept: current.categoryId,
      suggested: suggestion.categoryId ?? 'uncategorized',
    });
  }

  const form: EntryForm = {
    ...current,
    // The sign travels with the amount: it is the one field the suggestion's own amount carries.
    kind: amountUntouched ? (suggestedMinor < 0n ? 'expense' : 'income') : current.kind,
    amount: amountUntouched ? suggestedAmount : current.amount,
    description: suggestion.description,
    accountId: options.accountTouched ? current.accountId : suggestion.accountId,
    categoryId: current.categoryId === null ? suggestion.categoryId : current.categoryId,
  };

  const parts = [`Filled ${applied.join(', ')} from “${suggestion.description}”.`];
  if (kept.length !== 0) {
    parts.push(
      `Kept your ${kept.map((field) => `${field.field} (${notedValue(options, field)})`).join(' and ')} rather than the suggestion’s.`,
    );
  }
  parts.push('The dates and the paid state were left exactly as they were.');

  return { form, applied, kept, note: parts.join(' ') };
}
